import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { diaryService } from "@/services/diaryService";
import { getEmotionById, Emotion } from "@/constants/emotions";
import { RecordCard } from "@/components/dataDisplay/cards/RecordCard";
import theme from "@/constants/theme";

interface DiaryEntry {
  id: string;
  audioUrl: string;
  createdAt: string;
  description: string;
  feeling: string;
  userId: string;
  advice: string;
}

export default function RecordsTabContent() {
  const { token } = useAuth();
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const fetchEntries = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);

    try {
      const response = await diaryService.getEntries(token!, nextCursor);
      const newData = response.data ?? [];

      setEntries((prevEntries) => {
        const existingIds = new Set(prevEntries.map((item) => item.id));
        const uniqueNewEntries = newData.filter(
          (item: DiaryEntry) => !existingIds.has(item.id)
        );
        return [...prevEntries, ...uniqueNewEntries];
      });

      if (response.nextCursor && newData.length > 0) {
        setNextCursor(response.nextCursor);
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error cargando registros:", err);
    } finally {
      setIsLoading(false);
    }
  }, [token, nextCursor, isLoading, hasMore]);

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const sentinel = sentinelRef.current;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          fetchEntries();
        }
      },
      { threshold: 1.0 }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current && sentinel) {
        observerRef.current.unobserve(sentinel);
      }
    };
  }, [fetchEntries, isLoading, hasMore]);

  return (
    <div
      style={{
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        paddingBottom: "32px",
      }}
    >
      {entries.map((item) => {
        const emotionData = getEmotionById(item.feeling as Emotion["id"]);
        if (!emotionData) return null;
        return (
          <RecordCard
            key={item.id}
            comment={item.description}
            advice={item.advice}
            date={new Date(item.createdAt).toLocaleString("es-ES", {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
            emotion={emotionData?.label || item.feeling}
            icon={<emotionData.Icon style={{ width: 24, height: 24 }} />}
          />
        );
      })}

      <div ref={sentinelRef} style={{ height: 1 }} />

      {/* Loader */}
      {isLoading && (
        <div
          style={{
            textAlign: "center",
            padding: "1rem",
            color: theme.colors.text.caption,
          }}
        >
          <span>Cargando...</span>
        </div>
      )}
    </div>
  );
}
